import React, { useEffect, useRef, useState } from "react";
import {
  Box, Typography, Button, Switch, FormControlLabel, TextField,
  Chip, CircularProgress,
  Tabs, Tab, Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useSnackbar } from "notistack";
import {
  surface, border, textPrimary, textSecondary, textMuted, font, shadow,
  accent, success, warning, primaryButtonSx, secondaryButtonSx, inputSx,
  formatSubjectName, subjectsForRegion,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import {
  get_tutoring_profile_api, update_tutoring_profile_api, upload_tutoring_profile_photo_api,
  create_connect_onboarding_link_api, get_connect_status_api,
  get_school_tutors_config_api, request_marketplace_access_api,
} from "../../../DAL/SchoolPortal/Tutors";
import TutorsOnboardingModal from "../../../components/TutorsOnboardingModal";
import { PaymentFlowIllustration, CalendarGridIllustration, AcceptDeclineIllustration } from "../../../components/tutorsOnboardingIllustrations";
import { DEFAULT_WORKING_HOURS } from "./WeekAvailabilityCalendar";
import WorkingHoursDialog from "./WorkingHoursDialog";
import { currencySymbol, regionOf } from "../../../utils/region";
import { describeRule } from "./InstructorPay";
import { useSchool } from "../../../Hooks/SchoolContext";

/** Monday first — the week an instructor plans, not the week Date.getDay() counts. */
const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const INSTRUCTOR_ONBOARDING_SLIDES = [
  {
    illustration: <PaymentFlowIllustration />,
    title: "Get paid automatically",
    description: "Students pay through XAir — once a lesson is confirmed, your share lands in your bank account a few days later. No invoicing, no chasing.",
  },
  {
    illustration: <CalendarGridIllustration />,
    title: "You're bookable by default",
    description: "Set your working hours and subjects, and students can book you across all of it automatically. Use the calendar only when you need to close off a time or reserve one for a specific student.",
  },
  {
    illustration: <AcceptDeclineIllustration />,
    title: "You're always in control",
    description: "Every request needs your OK first. The student's card is only held, not charged — you only get paid once you accept, and nothing moves if you decline. Want to be found by students at other schools? That needs your Owner or Manager's approval.",
  },
];

const card = { backgroundColor: surface, border: `1px solid ${border}`, borderRadius: "14px", boxShadow: shadow, p: { xs: 2, sm: 3 } };
const sectionTitle = { fontSize: "15px", fontWeight: 700, color: textPrimary, fontFamily: font, mb: 2 };

// Must match PAYOUT_HOLD_HOURS in web-backend/controllers/tutors/lessonPayout.js —
// the cron only runs every 30 min and doesn't fire the instant this elapses,
// so treat this as "expected around", not an exact promise.



/**
 * SETUP ONLY: working hours, subjects and pricing, profile, payouts.
 *
 * This was seven tabs — Today, My students, Classes, Calendar, Subjects &
 * Pricing, Profile, History & Earnings. The tab count was the symptom; the
 * disease was filing by NOUN. Every one of them genuinely was "about
 * teaching", which is why the label absorbed them and why nothing in the
 * structure would ever have rejected an eighth. A tab bar also asserts its
 * items are equally likely, and Today was opened perhaps twenty times a month
 * against Profile once in a career.
 *
 * The work half lives on Teaching.jsx. What was left behind kept rendering the
 * week grid and a column of bookings beside it, so there were two pages
 * showing one week — and this was the copy WITHOUT the lessons drawn on it.
 * The grid is gone; the working-hours PATTERN stays, because which days you
 * work is setup while blocking out next Friday is work.
 *
 * Everything guarded by the old `setupOnly` flag has been deleted rather than
 * hidden: SchoolTutors.jsx was its only other caller and nothing imported
 * SchoolTutors, so that half had been unreachable, not merely unused.
 */
export default function InstructorTutorsView() {
  const { enqueueSnackbar } = useSnackbar();
  const { isIndependentInstructor } = useSchool();
  const photoInputRef = useRef(null);

  // Three tabs, all of them setup. Today, My students and History & Earnings
  // were removed rather than hidden — see the note above.
  const [tab, setTab] = useState("calendar");
  const [profile, setProfile] = useState(null);
  const [pay, setPay] = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [schoolConfig, setSchoolConfig] = useState(null);
  const schoolRegion = regionOf(schoolConfig);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [workingHoursOpen, setWorkingHoursOpen] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    // Three requests, not seven. Availability, bookings, earnings and the
    // student list were all fetched for a half of this page that no longer
    // exists — and, before that, for tabs that were not the one on screen.
    const [profileRes, connectRes, configRes] = await Promise.all([
      get_tutoring_profile_api(),
      get_connect_status_api(),
      get_school_tutors_config_api(),
    ]);
    if (profileRes.code === 200) {
      setProfile(profileRes.data);
      // What the school has set this person's pay to, already resolved through
      // their own arrangement, the school default and the legacy fields — the
      // instructor gets one answer instead of three settings to combine.
      setPay(profileRes.compensation || null);
      if (!profileRes.data.tutoring?.onboardingSeen) setShowOnboarding(true);
    }
    if (connectRes.code === 200) setConnectStatus(connectRes.data);
    if (configRes.code === 200) setSchoolConfig(configRes.data);
    setLoading(false);
  };

  const handleRequestAccess = async () => {
    setRequestingAccess(true);
    const res = await request_marketplace_access_api();
    if (res.code === 200) {
      setProfile((prev) => ({ ...prev, tutoring: res.data }));
      enqueueSnackbar("Request sent to your school", { variant: "success" });
    } else {
      enqueueSnackbar(res.message || "Couldn't send the request", { variant: "error" });
    }
    setRequestingAccess(false);
  };

  const dismissOnboarding = async () => {
    setShowOnboarding(false);
    await update_tutoring_profile_api({ onboardingSeen: true });
  };

  const handleConnectClick = async () => {
    const res = await create_connect_onboarding_link_api("/school/teaching");
    if (res.code === 200 && res.data?.url) {
      window.location.href = res.data.url;
    } else {
      enqueueSnackbar(res.message || "Couldn't start Stripe onboarding", { variant: "error" });
    }
  };

  const saveProfile = async (updates) => {
    setSavingProfile(true);
    const res = await update_tutoring_profile_api(updates);
    if (res.code === 200) {
      setProfile((prev) => ({ ...prev, tutoring: res.data }));
    } else {
      enqueueSnackbar(res.message || "Failed to update profile", { variant: "error" });
    }
    setSavingProfile(false);
    return res.code === 200;
  };

  const toggleSubject = (subject) => {
    const current = profile?.tutoring?.subjects || [];
    const next = current.includes(subject) ? current.filter((s) => s !== subject) : [...current, subject];
    setProfile((prev) => ({ ...prev, tutoring: { ...prev.tutoring, subjects: next } }));
  };

  const handleSaveWorkingHours = async (hours) => {
    const ok = await saveProfile({ workingHours: hours });
    if (ok) {
      enqueueSnackbar("Working hours updated", { variant: "success" });
      setWorkingHoursOpen(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append("photo", file);
    const res = await upload_tutoring_profile_photo_api(formData);
    if (res.code === 200) {
      setProfile((prev) => ({ ...prev, tutoring: { ...prev.tutoring, photoUrl: res.data.photoUrl } }));
      enqueueSnackbar("Photo updated", { variant: "success" });
    } else {
      enqueueSnackbar(res.message || "Failed to upload photo", { variant: "error" });
    }
    setUploadingPhoto(false);
    e.target.value = "";
  };

  const setDefaultRateLocal = (durationKey, value) => {
    setProfile((prev) => ({ ...prev, tutoring: { ...prev.tutoring, rates: { ...prev.tutoring?.rates, default: { ...prev.tutoring?.rates?.default, [durationKey]: value } } } }));
  };
  const commitDefaultRate = (durationKey, value) => {
    const rates = profile?.tutoring?.rates || {};
    saveProfile({ rates: { ...rates, default: { ...rates.default, [durationKey]: value ? parseFloat(value) : undefined } } });
  };

  const setSubjectRateLocal = (subject, durationKey, value) => {
    setProfile((prev) => {
      const bySubject = prev.tutoring?.rates?.bySubject || {};
      return { ...prev, tutoring: { ...prev.tutoring, rates: { ...prev.tutoring?.rates, bySubject: { ...bySubject, [subject]: { ...bySubject[subject], [durationKey]: value } } } } };
    });
  };
  const commitSubjectRate = (subject, durationKey, value) => {
    const rates = profile?.tutoring?.rates || {};
    const bySubject = rates.bySubject || {};
    saveProfile({ rates: { ...rates, bySubject: { ...bySubject, [subject]: { ...bySubject[subject], [durationKey]: value ? parseFloat(value) : undefined } } } });
  };






  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: accent }} /></Box>;
  }

  const payoutConnected = Boolean(connectStatus?.onboardingComplete);
  // Under payoutMode "school" the SCHOOL is paid and distributes; the
  // instructor never needs their own Connect account. Showing them "students
  // can't book you until Stripe is connected" is simply false there — they are
  // already bookable — and it sends them down a two-minute setup that ends in
  // an account nothing will ever pay into.
  const payoutIsSchools = schoolConfig?.payoutMode === "school";
  const needsOwnPayout = !payoutConnected && !payoutIsSchools;

  return (
    <>
      <TutorsOnboardingModal open={showOnboarding} onClose={dismissOnboarding} slides={INSTRUCTOR_ONBOARDING_SLIDES} />
      <WorkingHoursDialog
        open={workingHoursOpen}
        initialValue={profile?.tutoring?.workingHours || DEFAULT_WORKING_HOURS}
        onClose={() => setWorkingHoursOpen(false)}
        onSave={handleSaveWorkingHours}
        saving={savingProfile}
      />

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{
          mb: 3, minHeight: "auto", borderBottom: `1px solid ${border}`,
          "& .MuiTab-root": { fontFamily: font, fontWeight: 700, fontSize: "13px", textTransform: "none", minHeight: "auto", py: 1.25, color: textSecondary },
          "& .Mui-selected": { color: `${accent} !important` },
          "& .MuiTabs-indicator": { backgroundColor: accent, height: "2.5px" },
        }}
      >
        {/* A class and a 1:1 are both a thing you are teaching, so they belong
            on one surface. This was its own nav item until the instructor
            became the person who accepts a class rather than the person one is
            scheduled onto. */}
        {/* Group classes are school-scoped and the create form has an
            INSTRUCTOR PICKER — so a CFI here was looking at, and able to cancel,
            every instructor's classes on a page framed as their own work. It is
            a school scheduling console, and it went back to its own nav item
            where the receptionist can reach it too. */}
        <Tab value="calendar" label="Working hours" />
        <Tab value="pricing" label="Subjects & Pricing" />
        <Tab value="profile" label="Profile" />
      </Tabs>





      {tab === "calendar" && (
        <>
          {needsOwnPayout && (
            <Box sx={{ ...card, mb: 3, borderColor: warning, backgroundColor: `${warning}0d` }}>
              <Typography sx={{ ...sectionTitle, mb: 0.75 }}>Finish setting up payouts</Typography>
              <Typography sx={{ fontSize: "13px", color: textSecondary, fontFamily: font, mb: 1.5 }}>
                Students can't book you until Stripe is connected — this takes about two minutes.
              </Typography>
              <Button onClick={handleConnectClick} sx={primaryButtonSx}>Set up payouts</Button>
            </Box>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 3, alignItems: "start" }}>
            <Box sx={card}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Typography sx={{ ...sectionTitle, mb: 0 }}>Working hours</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Button
                    size="small"
                    startIcon={<ScheduleIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setWorkingHoursOpen(true)}
                    sx={{ ...secondaryButtonSx, py: 0.5, px: 1.5, fontSize: "12px", minHeight: "auto" }}
                  >
                    Working hours
                  </Button>
                  {payoutConnected && (
                    <FormControlLabel
                      labelPlacement="start"
                      control={<Switch size="small" checked={!profile?.tutoring?.isBookable} onChange={(e) => saveProfile({ isBookable: !e.target.checked })} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: warning }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: warning } }} />}
                      label={<Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary }}>Close all dates</Typography>}
                      sx={{ ml: 0 }}
                    />
                  )}
                </Box>
              </Box>
              {!profile?.tutoring?.isBookable && (
                <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: warning, mb: 1.5, fontWeight: 600 }}>
                  You're closed to new bookings. Lessons already confirmed aren't affected.
                </Typography>
              )}
              {/* THE GRID LIVES ON TEACHING. It draws lessons as well as
                  availability there, so keeping a second copy here meant two
                  pages with a calendar and a column of bookings — and the one
                  without the lessons on it was the worse of the two. What
                  stays here is the PATTERN: which days you work and until
                  when. Blocking out next Friday is work, and happens on the
                  diary. */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {DAY_ORDER.map((d) => {
                  const cfg = (profile?.tutoring?.workingHours || DEFAULT_WORKING_HOURS)[d];
                  return (
                    <Box key={d} sx={{
                      px: 1.25, py: 0.9, borderRadius: "9px", minWidth: 92,
                      border: `1px solid ${border}`,
                      backgroundColor: cfg?.enabled ? "transparent" : "rgba(0,0,0,0.03)",
                    }}>
                      <Typography sx={{
                        fontFamily: font, fontSize: "10px", fontWeight: 800, letterSpacing: "0.07em",
                        textTransform: "uppercase", color: cfg?.enabled ? textSecondary : textMuted,
                      }}>
                        {d.slice(0, 3)}
                      </Typography>
                      <Typography sx={{
                        fontFamily: font, fontSize: "12.5px", fontWeight: 700,
                        color: cfg?.enabled ? textPrimary : textMuted,
                        fontVariantNumeric: "tabular-nums",
                      }}>
                        {cfg?.enabled ? `${cfg.start}–${cfg.end}` : "Closed"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

          </Box>
        </>
      )}

      {tab === "pricing" && profile && (
        <Box sx={card}>
          {/* WHAT YOU ARE PAID, above what students are charged.
              They are different numbers, and an instructor reading a rate card
              has no way to tell what lands in their account — a 70% share of a
              $150 session and a flat $60 are both compatible with the same
              price list. Read-only: it is the school's arrangement with them,
              set in Settings by an Owner. */}
          {pay && (
            <Box sx={{ mb: 3, p: 1.75, border: `1px solid ${border}`, borderRadius: "10px", backgroundColor: "#FAF8F5" }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: textMuted, fontFamily: font, textTransform: "uppercase", mb: 1 }}>
                What you're paid
              </Typography>
              {[["1:1 sessions", pay.oneToOne], ["Group classes", pay.group]].map(([what, rule]) => (
                <Box key={what} sx={{ display: "flex", alignItems: "baseline", gap: 1, py: 0.4, flexWrap: "wrap" }}>
                  <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, width: 110, flexShrink: 0 }}>
                    {what}
                  </Typography>
                  <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary }}>
                    {describeRule(rule, currencySymbol(schoolRegion))}
                  </Typography>
                  {/* Whose arrangement this is. Without it, a figure that moves
                      when the school changes its default looks like an error. */}
                  {rule?.source !== "instructor" && (
                    <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted }}>
                      school default
                    </Typography>
                  )}
                </Box>
              ))}
              <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, mt: 1 }}>
                Set by your school. The prices below are what students pay, which is a different number.
              </Typography>
            </Box>
          )}
          <Typography sx={sectionTitle}>Subjects & pricing</Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: textMuted, fontFamily: font, textTransform: "uppercase", mb: 1 }}>Subjects you cover</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
            {subjectsForRegion(schoolRegion).map((subject) => {
              const selected = profile.tutoring?.subjects?.includes(subject);
              return (
                <Chip
                  key={subject}
                  label={formatSubjectName(subject)}
                  onClick={() => toggleSubject(subject)}
                  sx={{
                    fontFamily: font, fontWeight: 600, cursor: "pointer",
                    backgroundColor: selected ? accent : "transparent",
                    color: selected ? "#fff" : textSecondary,
                    border: `1px solid ${selected ? accent : border}`,
                  }}
                />
              );
            })}
          </Box>
          <Button size="small" onClick={() => saveProfile({ subjects: profile.tutoring?.subjects || [] })} disabled={savingProfile} sx={{ ...secondaryButtonSx, mb: 2.5, py: 0.75, px: 2 }}>
            Save subjects
          </Button>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            {/* No lesson-format choice: every lesson is online. The server
                refuses anything else (tutoring_profile.js), so offering the
                option would only produce an error. */}
            <TextField
              fullWidth label={`Your hourly rate (${currencySymbol(schoolRegion)})`} type="number" sx={inputSx}
              disabled={schoolConfig?.rateMode !== "instructor_set"}
              value={profile.tutoring?.hourlyRate ?? (schoolConfig?.rateMode !== "instructor_set" ? schoolConfig?.defaultHourlyRate ?? "" : "")}
              onChange={(e) => setProfile((prev) => ({ ...prev, tutoring: { ...prev.tutoring, hourlyRate: e.target.value } }))}
              onBlur={(e) => saveProfile({ hourlyRate: e.target.value ? parseFloat(e.target.value) : undefined })}
              helperText={
                // An independent instructor IS the admin of their own group, so
                // "ask your school admin to update it" was telling them to ask
                // themselves, about a setting they can reach in two clicks.
                schoolConfig?.rateMode === "org_standard"
                  ? (isIndependentInstructor
                      ? "You're on one standard rate for every session"
                      : "Your school uses one standard rate for everyone")
                  : schoolConfig?.rateMode === "admin_set"
                    ? (isIndependentInstructor
                        ? "This one is set in your group's settings, not here"
                        : "Your school admin sets this — ask them to update it")
                    : (isIndependentInstructor
                        ? "Leave blank to use your group's default rate"
                        : "Leave blank to use your school's default rate")
              }
            />
          </Box>

          {schoolConfig?.rateMode === "instructor_set" && (
            <Box sx={{
              mt: 3, pt: 2.5, borderTop: `1px solid ${border}`,
              // The pricing table is three fixed 90px columns plus a label, so
              // it is wider than a phone. It scrolls inside its OWN container:
              // a wide table that takes the whole page sideways leaves an
              // instructor at an airfield unable to reach anything else.
              "& > .rate-grid": { overflowX: "auto" },
            }}>
              <Typography sx={{ fontFamily: font, fontSize: "14px", fontWeight: 700, color: textPrimary, mb: 0.5 }}>Session pricing</Typography>
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mb: 2 }}>
                Set a price per session length. Per-subject prices override your default below when set.
              </Typography>

              <Box className="rate-grid" sx={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) repeat(3, 90px)", gap: 1, mb: 1, minWidth: 400 }}>
                <Box />
                {["10 min", "30 min", "60 min"].map((label) => (
                  <Typography key={label} sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, color: textMuted, textAlign: "center" }}>{label}</Typography>
                ))}
              </Box>

              <Box className="rate-grid" sx={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) repeat(3, 90px)", gap: 1, alignItems: "center", py: 1, borderBottom: `1px solid ${border}`, minWidth: 400 }}>
                <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 600, color: textPrimary }}>Default</Typography>
                {["min10", "min30", "min60"].map((key) => (
                  <TextField
                    key={key} size="small" type="number" sx={inputSx}
                    value={profile.tutoring?.rates?.default?.[key] ?? ""}
                    onChange={(e) => setDefaultRateLocal(key, e.target.value)}
                    onBlur={(e) => commitDefaultRate(key, e.target.value)}
                  />
                ))}
              </Box>

              {(profile.tutoring?.subjects || []).map((subject) => (
                <Box key={subject} className="rate-grid" sx={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) repeat(3, 90px)", gap: 1, alignItems: "center", py: 1, borderBottom: `1px solid ${border}`, minWidth: 400 }}>
                  <Typography sx={{ fontFamily: font, fontSize: "13px", color: textPrimary }}>{formatSubjectName(subject)}</Typography>
                  {["min10", "min30", "min60"].map((key) => (
                    <TextField
                      key={key} size="small" type="number" placeholder={String(profile.tutoring?.rates?.default?.[key] ?? "")} sx={inputSx}
                      value={profile.tutoring?.rates?.bySubject?.[subject]?.[key] ?? ""}
                      onChange={(e) => setSubjectRateLocal(subject, key, e.target.value)}
                      onBlur={(e) => commitSubjectRate(subject, key, e.target.value)}
                    />
                  ))}
                </Box>
              ))}

              {(profile.tutoring?.subjects || []).length === 0 && (
                <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textMuted, mt: 1 }}>
                  Add subjects above to set a price per subject — otherwise your default applies to all of them.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {tab === "profile" && profile && (
        <Box sx={card}>
          <Typography sx={sectionTitle}>Profile</Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={profile.tutoring?.photoUrl}
                sx={{ width: 72, height: 72, backgroundColor: `${accent}1a`, color: accent, fontFamily: font, fontWeight: 700, fontSize: "22px" }}
              >
                {profile.first_name?.[0]}{profile.last_name?.[0]}
              </Avatar>
              <Box
                onClick={() => !uploadingPhoto && photoInputRef.current?.click()}
                sx={{
                  position: "absolute", bottom: -2, right: -2, width: 26, height: 26, borderRadius: "50%",
                  backgroundColor: accent, display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", border: `2px solid ${surface}`,
                }}
              >
                {uploadingPhoto ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : <EditIcon sx={{ fontSize: 13, color: "#fff" }} />}
              </Box>
              <input ref={photoInputRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={handlePhotoChange} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: font, fontSize: "15px", fontWeight: 700, color: textPrimary }}>{profile.first_name} {profile.last_name}</Typography>
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary }}>
                A photo helps students recognize and trust you — especially in the open marketplace.
              </Typography>
            </Box>
          </Box>

          <TextField
            fullWidth multiline minRows={2} label="Bio" sx={{ ...inputSx, mb: 2.5 }}
            value={profile.tutoring?.bio || ""}
            onChange={(e) => setProfile((prev) => ({ ...prev, tutoring: { ...prev.tutoring, bio: e.target.value } }))}
            onBlur={(e) => saveProfile({ bio: e.target.value })}
          />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, pb: 2.5, borderBottom: `1px solid ${border}` }}>
            <Typography sx={{ fontFamily: font, fontSize: "13px", color: textSecondary }}>Stripe payouts</Typography>
            {payoutConnected ? (
              <Chip label="Connected" sx={{ backgroundColor: `${success}1a`, color: success, fontWeight: 700, fontFamily: font }} />
            ) : (
              <Button size="small" onClick={handleConnectClick} sx={{ ...secondaryButtonSx, py: 0.75, px: 2 }}>Set up payouts</Button>
            )}
          </Box>

          {schoolConfig?.allowOpenMarketplace && (
            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontFamily: font, fontSize: "14px", fontWeight: 700, color: textPrimary, mb: 0.5 }}>Marketplace visibility</Typography>
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mb: 1.5 }}>
                Your school has the open marketplace on. Becoming visible to students at other schools needs your Owner or Manager's approval.
              </Typography>
              {profile.tutoring?.marketplaceAccessStatus === "approved" && (
                <Chip label="Marketplace visible" sx={{ backgroundColor: `${success}1a`, color: success, fontWeight: 700, fontFamily: font }} />
              )}
              {profile.tutoring?.marketplaceAccessStatus === "pending" && (
                <Chip label="Request pending" sx={{ backgroundColor: `${warning}1a`, color: warning, fontWeight: 700, fontFamily: font }} />
              )}
              {(!profile.tutoring?.marketplaceAccessStatus || ["not_requested", "denied"].includes(profile.tutoring.marketplaceAccessStatus)) && (
                <Button onClick={handleRequestAccess} disabled={requestingAccess} sx={{ ...secondaryButtonSx, py: 0.75, px: 2 }}>
                  {profile.tutoring?.marketplaceAccessStatus === "denied" ? "Request again" : "Request marketplace access"}
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

    </>
  );
}
